import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  dateRangeTypes,
  listTypes,
  orderEnrichedMatchesByDate,
  getYear,
  composeMatchStatus,
  statusTypes,
  composeDateRangeStartAndEnd,
  useHash,
  getHashQueryObject,
  composeForgeClubs,
  composeAllOption,
  getCurrentDatePoint,
  getNewCurrentDatePoint,
} from "./utils";
import useModal from "../shared/hooks/useModal";
import ToggleButton from "../shared/components/ToggleButton";
import { Chevron } from "../shared/svg";
import MatchGroup from "./components/MatchGroup";
import Dropdown from "../shared/components/Dropdown";
import CalendarIcon from "../shared/svg/CalendarIcon/CalendarIcon";
import {
  directionTypes,
  filterTypes,
  mlsPhaseTypes,
} from "../shared/utils";
import Calendar from "../shared/components/Calendar";
import { chevronDirection, NoResults } from "../shared/svg/Svg";
import { useReactAppState } from "../shared/containers/utils";
import { trackEvent, trackWeekSelector } from '../../libraries/_modules/tracking/helpers';
import MatchGroupLoader from "./components/MatchGroup/loader";
import { format, isAfter, isBefore } from "date-fns";
import { MatchListWrapper } from './v2/MatchListWrapper/MatchListWrapper';
import { ThemeProvider } from 'styled-components';
import { Theme, themeTypes } from '../shared/styled/Theme';
import useForgeMatches from './hooks/useForgeMatches';
import useMLSMatches from './hooks/useMLSMatches';
import useMlsSeasons from './hooks/useMlsSeasons';
import useMLSCompetitions from './hooks/useMLSCompetitionts';
import useSeasonYears from './hooks/useSeasonYears';

function MatchList({ options }) {
  const { mediaQueries, t, urlList } = useReactAppState();
  const { calendarUrl } = urlList || {};
  const { isMediumLargeMin } = mediaQueries || {};
  const { hideMatchPromos, hideCompetitionsDropdown, hideClubsDropdown } = options;
  const mlsPhaseType = options.mlsPhaseType || mlsPhaseTypes.regular;
  const listType = options.listType || listTypes.matches;
  const vsmDateRangeType = options?.dateRangeType || dateRangeTypes.week;

  const [hashQuery, setHashQuery] = useHash();

  const [filters, setFilters] = useState(() => {
    const hashQueryObject = getHashQueryObject(hashQuery);

    return {
      competitionSportecId: composeAllOption(hashQueryObject?.competition || options.competitionSportecId || filterTypes.all),
      clubSportecId: composeAllOption(hashQueryObject?.club || options.clubSportecId || filterTypes.all),
      clubs: composeForgeClubs(options?.allClubs?.items),
      currentDatePoint: getCurrentDatePoint(hashQueryObject?.date || options.currentDatePoint || new Date()),
    }
  });

  const { currentDatePoint, competitionSportecId, clubSportecId, clubs } = filters;

  const headerRef = useRef(null);
  const calendarRef = useRef(null);
  const scheduleTypeClass = `mls-c-schedule--${listType}`;

  const [dateRangeType, setDateRangeType] = useState(options?.dateRangeType || dateRangeTypes.week);
  const [nextRange, setNextRange] = useState(null);
  const [prevRange, setPrevRange] = useState(null);
  const [firstForce, setFirstForce] = useState(options?.forceWeekly);

  const [refetchInterval, setRefetchInterval] = useState(null);
  const [currentYear, setCurrentYear] = useState(() => getYear(currentDatePoint));

  const {
    currentRangeYear,
    currentRangeMonth,
    currentRangeWeek,
    nextRangeYear,
    nextRangeMonth,
    nextRangeWeek,
    prevRangeYear,
    prevRangeMonth,
    prevRangeWeek,
    isoStringRS,
    isoStringRE
  } = React.useMemo(() => composeDateRangeStartAndEnd(currentDatePoint, dateRangeType), [currentDatePoint, dateRangeType]);

  const { data: mlsSeasons } = useMlsSeasons({ competitionSportecId });
  const { data: mlsCompetitions, isLoading: isLoadingMlsCompetitions, isError: isErrorMlsCompetitions } = useMLSCompetitions();


  const seasonSportecId = useMemo(() => {
    const currentDateYear = getYear(currentDatePoint);
    return mlsSeasons?.get(Number(currentDateYear))?.season_id
  }, [currentDatePoint, mlsSeasons]);


  const { data: mlsMatches, isLoading: isLoadingMatches } = useMLSMatches({
    isoStringRS,
    isoStringRE,
    seasonSportecId,
    competitionSportecId,
    clubSportecId,
    matchType: mlsPhaseType,
    forceWeekly: options?.forceWeekly,
    firstForce,
    currentDatePoint,
    dateRangeType,
    refetchInterval,
  });

  const { data: d3Matches, isLoading: isLoadingD3Matches } = useForgeMatches({ mlsMatches });

  const orderedMatches = React.useMemo(() => orderEnrichedMatchesByDate({
    dateRangeType, mlsMatches, competitionSportecId, clubSportecId, isoStringRS, isoStringRE
  }), [dateRangeType, mlsMatches, competitionSportecId, clubSportecId, isoStringRS, isoStringRE]);

  useEffect(() => {
    if (currentDatePoint) {
      setCurrentYear(getYear(currentDatePoint));
    }
  }, [currentDatePoint]);

  useEffect(() => {
    if (options.forceWeekly && (competitionSportecId === filterTypes.allCompetitions)) {
      setDateRangeType(dateRangeTypes.week);
      setPrevRange(prevRangeWeek);
      setNextRange(nextRangeWeek);
      setFirstForce(false);
    } else {
      if (options.forceWeekly && firstForce) {
        setFirstForce(false);
      }

      if (vsmDateRangeType === dateRangeTypes.year) {
        setDateRangeType(dateRangeTypes.year);
        setPrevRange(prevRangeYear);
        setNextRange(nextRangeYear);
      }

      if (vsmDateRangeType === dateRangeTypes.month) {
        setDateRangeType(dateRangeTypes.month);
        setPrevRange(prevRangeMonth);
        setNextRange(nextRangeMonth);
      }

      if (vsmDateRangeType === dateRangeTypes.week) {
        setDateRangeType(dateRangeTypes.week);
        setPrevRange(prevRangeWeek);
        setNextRange(nextRangeWeek);
      }
    }
  }, [clubSportecId, mlsPhaseType, competitionSportecId, nextRange, prevRange]);

  const syncToCalendarText = t("match_sync_to_calendar");

  const interval = React.useMemo(() => {
    if (orderedMatches && orderedMatches.length > 0) {
      return orderedMatches.reduce((total, outerCurrent) => {
        if (dateRangeType !== dateRangeTypes.year && outerCurrent.matches && outerCurrent.matches.length > 0) {
          const matchesTotal = outerCurrent.matches.reduce((accum, current) => {
            const status = composeMatchStatus({ period: current?.period, delayedMatch: current?.delayedMatch });


            if (status === statusTypes.pre) {
              return Math.min(accum, 300000);
            }

            if (status === statusTypes.live) {
              return Math.min(30000);
            }

            return accum;
          }, 30000);

          return Math.min(total, matchesTotal);
        }

        return total;
      }, -1);
    }

    return null;
  }, [orderedMatches]);


  const updateFilter = useCallback(({ name, value }) => {
    setFilters(currentFilters => ({
      ...currentFilters,
      [name]: value
    }))
  }, [setFilters]);

  useEffect(() => {
    if (interval && interval <= 300000 && interval > 0) {
      setRefetchInterval(interval);
    }
  }, [interval]);

  const { toggleModal: toggleCalendar, isOpen: isCalendarOpen, closeModal: closeCalendarModel, Modal: CalendarModal } = useModal({
    bindTo: calendarRef.current,
    customStyle: `
      position: absolute;
      left: 0;
      background: #ffffff;
      z-index: 1000;
    `
  });

  const handleDateRangeSelection = React.useCallback((direction, position) => (e) => {
    if (direction === directionTypes.next || e.target.value === directionTypes.next) {
      const nextRangeDate = dateRangeType === dateRangeTypes.week ? nextRangeWeek : (dateRangeType === dateRangeTypes.year ? nextRangeYear : nextRangeMonth);
      updateFilter({
        name: "currentDatePoint",
        value: nextRangeDate.dateRangeStart
      })

      trackWeekSelector({
        selectorLocation: position,
        direction: 'forward',
        selection: nextRangeDate.formattedDateRangeStringIntl
      });
    }
    else {
      const prevRangeDate = dateRangeType === dateRangeTypes.week ? prevRangeWeek : (dateRangeType === dateRangeTypes.year ? prevRangeYear : prevRangeMonth);
      updateFilter({
        name: "currentDatePoint",
        value: prevRangeDate.dateRangeStart
      })

      trackWeekSelector({
        selectorLocation: position,
        direction: 'back',
        selection: prevRangeDate.formattedDateRangeStringIntl
      });
    }

    if (headerRef?.current) {
      headerRef.current.scrollIntoView({ block: 'center' });
    }
  }, [dateRangeType, currentDatePoint]);

  const handleCalendarSyncClick = React.useCallback(() => {
    trackEvent('sync-to-calendar-click');
  }, []);

  useEffect(() => {
    const { competitionSportecId, clubSportecId, currentDatePoint } = filters;

    if (!options?.disableDeeplinking) {
      const competitionSlugHash = competitionSportecId;
      const clubSportecIdHash = clubSportecId;
      const competitionQuery = !hideCompetitionsDropdown ? `competition=${competitionSlugHash}&` : "";
      const clubQuery = !hideClubsDropdown ? `club=${clubSportecIdHash}&` : "";
      const dateQuery = `date=${format(currentDatePoint, "yyyy-MM-dd")}&`;
      const fullQuery = `${competitionQuery}${clubQuery}${dateQuery}`;

      if (fullQuery) {
        const hashQuery = fullQuery.slice(0, fullQuery.length - 1);
        setHashQuery(hashQuery);
      }
    }

  }, [filters, options?.disableDeeplinking, options?.hideCompetitionsDropdown, options?.hideClubsDropdown]);

  const { seasonFirstYear, seasonLastYear } = useSeasonYears({ mlsSeasons });

  useEffect(() => {
    const hashQueryObject = getHashQueryObject(hashQuery);

    setFilters(currentFilters => {
      const newCurrentDatePoint = getNewCurrentDatePoint({
        currentDatePoint: currentFilters?.currentDatePoint,
        hashQueryObject, seasonFirstYear, seasonLastYear
      });

      return ({
        ...currentFilters,
        competitionSportecId: composeAllOption(hashQueryObject?.competition || currentFilters?.competitionSportecId),
        clubSportecId: composeAllOption(hashQueryObject?.club || currentFilters?.clubSportecId),
        currentDatePoint: newCurrentDatePoint,
      })
    });
  }, [hashQuery]);

  const isNextButtonDisabled = useMemo(() => {
    const nextRangeDate = dateRangeType === dateRangeTypes.week ? nextRangeWeek : (dateRangeType === dateRangeTypes.year ? nextRangeYear : nextRangeMonth);

    return isAfter(nextRangeDate.dateRangeStart, seasonLastYear);
  }, [dateRangeType, seasonLastYear, nextRangeWeek, nextRangeYear, nextRangeMonth]);

  const isPreviousButtonDisabled = useMemo(() => {
    const prevRangeDate = dateRangeType === dateRangeTypes.week ? prevRangeWeek : (dateRangeType === dateRangeTypes.year ? prevRangeYear : prevRangeMonth);

    return isBefore(prevRangeDate.dateRangeStart, seasonFirstYear);
  }, [dateRangeType, seasonFirstYear, prevRangeWeek, prevRangeYear, prevRangeMonth]);

  return (
    <div role="region" className={`mls-c-schedule ${scheduleTypeClass}`}>
      <div ref={headerRef} className="mls-c-schedule__header">
        <>
          <div className="mls-c-schedule__dropdown-group">
            <div className={`mls-o-buttons__two-way ${isCalendarOpen ? "mls-o-buttons__two-way--focused" : ""}`} role="group" aria-label="Date selector" tabIndex="0">
              <button value="prev" aria-label="Previous results"
                disabled={isPreviousButtonDisabled}
                onClick={isPreviousButtonDisabled ? null : handleDateRangeSelection(directionTypes.prev, 'top')}><Chevron direction={chevronDirection.left} /></button>
              <button onClick={(e) => toggleCalendar(e)}>
                {dateRangeType === dateRangeTypes.week
                  ? currentRangeWeek?.formattedDateRangeString
                  : (dateRangeType === dateRangeTypes.year ? getYear(currentRangeYear?.dateRangeStart) : currentRangeMonth?.formattedDateRangeString)}
              </button>
              <button value="next" aria-label="Next results"
                disabled={isNextButtonDisabled}
                onClick={isNextButtonDisabled ? null : handleDateRangeSelection(directionTypes.next, 'top')}><Chevron direction={chevronDirection.right} /></button>
            </div>
            {
              hideCompetitionsDropdown || isLoadingMlsCompetitions || isErrorMlsCompetitions
                ? null
                : (
                  <Dropdown
                    valueKey={"competition_id"}
                    displayKey={"competition_name"}
                    type={"competition"}
                    title={t("match_competitions_dropdown")}
                    onChangeFn={(value) => updateFilter({
                      name: "competitionSportecId",
                      value
                    })}
                    items={mlsCompetitions}
                    currentValue={competitionSportecId} />
                )
            }
            {
              hideClubsDropdown
                ? null
                : <Dropdown
                  onChangeFn={(value) => updateFilter({
                    name: "clubSportecId",
                    value
                  })}
                  displayKey={"name"}
                  type={"club"}
                  valueKey={"sportecId"}
                  items={clubs}
                  currentValue={clubSportecId} />
            }
            {
              !isMediumLargeMin && listType !== listTypes.odds && calendarUrl
                ? (
                  <ToggleButton
                    onClick={handleCalendarSyncClick}
                    target={"_blank"}
                    href={calendarUrl}
                    className="mls-c-schedule__calendar-sync mls-o-buttons__icon mls-o-buttons__icon--left">
                    <CalendarIcon />
                    <span className="mls-c-schedule__calendar-sync-text">{syncToCalendarText}</span>
                  </ToggleButton>
                )
                : null
            }
          </div>
          {
            isMediumLargeMin && listType !== listTypes.odds && calendarUrl
              ? (
                <ToggleButton
                  onClick={handleCalendarSyncClick}
                  target={"_blank"}
                  href={calendarUrl}
                  className="mls-c-schedule__calendar-sync mls-o-buttons__icon mls-o-buttons__icon--left">
                  <CalendarIcon />
                  <span className="mls-c-schedule__calendar-sync-text">{t("match_sync_to_calendar")}</span>
                </ToggleButton>
              )
              : null
          }
        </>
        <div className="mls-c-schedule__calendar" ref={calendarRef}>
          {isCalendarOpen
            ?
            (
              <CalendarModal>
                <Calendar
                  seasonSportecId={seasonSportecId}
                  competitionSportecId={competitionSportecId}
                  clubSportecId={clubSportecId}
                  mlsPhaseType={mlsPhaseType}
                  closeModal={closeCalendarModel}
                  setCurrentDatePoint={(date) => updateFilter({
                    name: "currentDatePoint",
                    value: date
                  })}
                  currentYear={currentYear}
                  setCurrentYear={setCurrentYear}
                  currentMonth={currentRangeMonth.dateRangeStart}
                  weekRange={dateRangeType === dateRangeTypes.week ? currentRangeWeek : null}
                  minDate={seasonFirstYear}
                  maxDate={seasonLastYear}
                />
              </CalendarModal>
            )
            : null
          }
        </div>
      </div>
      <div className="mls-c-schedule__matches">
        {
          isLoadingMatches || isLoadingD3Matches || isLoadingMlsCompetitions
            ? <MatchGroupLoader stripCount={3} />
            : orderedMatches?.length > 0
              ? (options.useNewMatchList ?
                <ThemeProvider theme={{ ...Theme, type: themeTypes.light }}>
                  <MatchListWrapper
                    showSecondaryTeams={options?.showSecondaryTeams}
                    hideMatchPromos={hideMatchPromos}
                    weekRange={currentRangeWeek}
                    matchList={orderedMatches}
                    d3Matches={d3Matches}
                    listType={listType}
                    dateRangeType={dateRangeType}
                    betMgmCompetitionIds={options?.betMgmCompetitionIds}
                    showClubRanks={options?.showClubRanks}
                    showGroupInformation={options?.showGroupInformation}
                    showRoundDetail={options?.showRoundDetail}
                    showCompetitionStage={options.showCompetitionStage}
                  />
                </ThemeProvider>
                :
                <MatchGroup
                  showSecondaryTeams={options?.showSecondaryTeams}
                  hideMatchPromos={hideMatchPromos}
                  weekRange={currentRangeWeek}
                  matchList={orderedMatches}
                  listType={listType}
                  dateRangeType={dateRangeType}
                  betMgmCompetitionIds={options?.betMgmCompetitionIds}
                />
              )
              : (
                <div className="mls-c-schedule__no-results">
                  <NoResults size="large" />
                  <div className="mls-c-schedule__no-results-text">{t("match_no_matches")}</div>
                </div>
              )
        }
      </div>
      {currentDatePoint === null ? null :
        <div className="mls-c-schedule__footer">
          <div className="mls-c-schedule__button-group">
            <ToggleButton
              className="mls-o-buttons__icon mls-o-buttons__icon--left"
              value="prev"
              disabled={isPreviousButtonDisabled}
              onClick={isPreviousButtonDisabled ? null : handleDateRangeSelection(directionTypes.prev, 'bottom')}>
              <Chevron direction={chevronDirection.left} />
              {dateRangeType === dateRangeTypes.week ? prevRangeWeek?.formattedDateRangeString : (dateRangeType === dateRangeTypes.year ? getYear(prevRangeYear?.dateRangeStart) : prevRangeMonth?.formattedDateRangeString)}
            </ToggleButton>
            <ToggleButton
              className="mls-o-buttons__icon mls-o-buttons__icon--right"
              value="next"
              disabled={isNextButtonDisabled}
              onClick={isNextButtonDisabled ? null : handleDateRangeSelection(directionTypes.next, 'bottom')}>
              {dateRangeType === dateRangeTypes.week ? nextRangeWeek?.formattedDateRangeString : (dateRangeType === dateRangeTypes.year ? getYear(nextRangeYear?.dateRangeStart) : nextRangeMonth?.formattedDateRangeString)}
              <Chevron direction={chevronDirection.right} />
            </ToggleButton>
          </div>
        </div>
      }
    </div>
  );
}

// eslint-disable-next-line no-func-assign
MatchList = React.memo(MatchList);

export default MatchList;
